import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  linkBankTransactionThunk,
  updateRemittanceAllocationsThunk,
} from '../../../../redux/services/cashApplicationService';
import type { BankTransactionRecord, ConfidenceFactor, RemittanceRecord } from '@dill/dill-shared';
import { depositUnaccountedAmount, isDepositOpenForLinking } from '@dill/dill-shared';
import { ConfidenceBadge } from '../components/ConfidenceBadge';
import { ChannelBadge } from '../components/ChannelBadge';
import { SettlementBadge } from '../components/SettlementBadge';
import { DillTable, DillButton, DillModal } from '../../../general/Dill';
import type { DillColumn, DillRowAction, DillSummaryCard } from '../../../general/Dill';
import { MatchedInvoicesTooltip } from '../components/MatchedInvoicesTooltip';
import { AddRemittanceModal } from './AddRemittanceModal';
import { AdjustAllocationModal } from './AdjustAllocationModal';
import { CaretDown, Clock, LinkSimple, PencilSimple, Plus, Trash, Warning } from 'phosphor-react';
import { openDillToast } from '../../../../utils/helpers';
import { formatCurrency, formatDate } from '../utils';
import { useDeleteRemittance } from './useDeleteRemittance';

/**
 * Advice we hold that the bank has not confirmed.
 *
 * Remittance advice is a claim of payment; until the deposit shows up, applying it to invoices
 * would mark them paid on a promise. Records wait here for that confirmation. Most are simply
 * in flight — ACH takes a few days — so overdue is flagged rather than filtered, otherwise
 * there is no view of cash on its way.
 */

// Mirrors SETTLEMENT_DATE_WINDOW_DAYS on the backend, which is where matching enforces it.
const SETTLEMENT_WINDOW_DAYS = 5;

/**
 * How near a deposit has to be to count as a possible match, deliberately looser than the
 * matcher.
 *
 * Automatic settlement is exact to the cent inside a 5-day window, so anything still sitting
 * here failed one of those two tests: a wire fee or short-pay moved the amount, or the advice
 * and the deposit are further apart in time than the matcher allows. A suggestion narrow enough
 * to agree with the matcher would therefore suggest nothing at all.
 *
 * The floor covers wire and lockbox fees, which are flat rather than proportional and so swamp
 * a percentage tolerance on small payments.
 */
const CANDIDATE_AMOUNT_TOLERANCE_PCT = 0.05;
const CANDIDATE_AMOUNT_TOLERANCE_FLOOR = 25;
const CANDIDATE_DATE_WINDOW_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseAdviceDate = (record: RemittanceRecord): Date | null => {
  // adviceDate is what the document claimed; paymentDate gets overwritten by the bank's date
  // on settlement, so it is only a fallback for records written before adviceDate existed.
  const raw = record.adviceDate ?? record.paymentDate;
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const daysWaiting = (record: RemittanceRecord, now: number): number | null => {
  const advice = parseAdviceDate(record);
  if (!advice) {
    return null;
  }
  return Math.max(0, Math.floor((now - advice.getTime()) / MS_PER_DAY));
};

interface DepositCandidate {
  deposit: BankTransactionRecord;
  // Deposit minus advice: positive means the bank received more than the advice claims.
  delta: number;
  /**
   * Deposit minus the whole document's total, for a record that arrived with others.
   *
   * Both are kept because they answer different questions and a bulk deposit only looks right
   * under the second: a $1,000 credit is $400 off a $600 check and exactly on the file those two
   * checks came from. Equal to `delta` when the record came in on its own.
   */
  batchDelta: number;
  // Absolute days between the two, so a deposit that landed before its advice still counts.
  dayGap: number | null;
  near: boolean;
  /** The deposit accounts for the file exactly, but not for this payment on its own. */
  coversFile: boolean;
}

interface AwaitingSettlementRow {
  id: string;
  payerName: string;
  /**
   * The Dill customer this resolved to, which is often not what the payer called itself — the
   * advice says "Suncore Construction and Materials, Inc." and the account is "SUNCORE (Sunroc
   * Corporation)". This is the one that will be credited, so it belongs in the table.
   */
  buyerName: string | null;
  buyerId: string | null;
  amount: number;
  adviceDate: string | null;
  waiting: number | null;
  overdue: boolean;
  channel: RemittanceRecord['channel'];
  matchStatus: RemittanceRecord['matchStatus'];
  settlementStatus: RemittanceRecord['settlementStatus'];
  matchConfidence: number | null;
  matchConfidenceFactors: ConfidenceFactor[];
  matchedInvoices: RemittanceRecord['matchedInvoices'];
  /**
   * Every still-waiting payment from the same document, this one included.
   *
   * A single-payment file gives a batch of one, so linking has one code path whether or not the
   * deposit turns out to be a bulk one.
   */
  batch: RemittanceRecord[];
  batchTotal: number;
  fileName: string | null;
  candidates: DepositCandidate[];
  record: RemittanceRecord;
}

const describeDeposit = (deposit: BankTransactionRecord): string =>
  deposit.merchantName || deposit.name || 'Bank deposit';

/**
 * Rank every open deposit against one waiting record, closest amount first.
 *
 * Every deposit is returned, not just the near ones: the near flag drives what the table
 * volunteers as a suggestion, while the full list is what the picker offers, because a lockbox
 * deposit covering several advices is arbitrarily far from any one of them and still the right
 * answer.
 *
 * Judged against this payment *and* against the whole document's total, ranked on whichever fits
 * better. A file of checks is banked as one credit, so measuring that credit only against one
 * check in the file reports it as wildly off and never `near` — which is the case the paragraph
 * above describes as arbitrarily far. Measuring only against the file's total has the opposite
 * fault: it hides a deposit that pays this one payment exactly. Both are computed, and the row
 * says which reading made it a candidate.
 */
const rankCandidates = (
  record: RemittanceRecord,
  deposits: BankTransactionRecord[],
  batchTotal: number
): DepositCandidate[] => {
  const adviceDate = record.adviceDate ?? record.paymentDate ?? null;
  const adviceTime = adviceDate ? new Date(adviceDate).getTime() : NaN;
  const toleranceFor = (amount: number) =>
    Math.max(CANDIDATE_AMOUNT_TOLERANCE_FLOOR, amount * CANDIDATE_AMOUNT_TOLERANCE_PCT);

  return deposits
    .map((deposit) => {
      const depositTime = new Date(deposit.date).getTime();
      const dayGap =
        Number.isNaN(adviceTime) || Number.isNaN(depositTime)
          ? null
          : Math.floor(Math.abs(depositTime - adviceTime) / MS_PER_DAY);
      /**
       * Measured against what the deposit still owes, not its face value.
       *
       * A $1,000 deposit already backing a $600 check has $400 left to explain, so a $400 advice
       * is an exact fit and the full amount would call it $600 short. Equal to the face value for
       * a deposit nothing is linked to yet, which is most of them.
       */
      const available = depositUnaccountedAmount(deposit);
      const delta = Math.round((available - record.amount) * 100) / 100;
      const batchDelta = Math.round((available - batchTotal) * 100) / 100;

      // An unknown date is not evidence against a match, so only a known, distant one excludes.
      const dateFits = dayGap === null || dayGap <= CANDIDATE_DATE_WINDOW_DAYS;
      const fitsPayment = Math.abs(delta) <= toleranceFor(record.amount);
      const fitsFile = Math.abs(batchDelta) <= toleranceFor(batchTotal);

      return {
        deposit,
        delta,
        batchDelta,
        dayGap,
        near: (fitsPayment || fitsFile) && dateFits,
        coversFile: fitsFile && !fitsPayment,
      };
    })
    .sort(
      (a, b) =>
        Math.min(Math.abs(a.delta), Math.abs(a.batchDelta)) -
          Math.min(Math.abs(b.delta), Math.abs(b.batchDelta)) || (a.dayGap ?? 0) - (b.dayGap ?? 0)
    );
};

export const AwaitingSettlementQueue = () => {
  const dispatch = useAppDispatch();
  const { awaitingSettlementItems, bankTransactions, openInvoices, loadingList } = useAppSelector(
    (state) => state.cashApplication
  );
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;
  const bankFeedLinked = currentSupplier?.cashAppBankFeed?.enabled === true;
  const loading = loadingList.includes('fetchRemittanceRecords');

  const [addingRecord, setAddingRecord] = useState(false);
  const [linkTarget, setLinkTarget] = useState<AwaitingSettlementRow | null>(null);
  const [linking, setLinking] = useState(false);
  /**
   * The candidate deposit whose payment list is open, and what is ticked in it.
   *
   * Per-deposit rather than global: the question "which payments does this deposit cover" has a
   * different answer for each one, and one expanded row at a time keeps the list readable.
   * Collapsed, a row links this payment alone; expanded, it links what is ticked.
   */
  const [expandedDepositId, setExpandedDepositId] = useState<string | null>(null);
  const [linkSelection, setLinkSelection] = useState<Set<string>>(new Set());
  /**
   * How the candidate deposits are ordered.
   *
   * Not reset when the picker reopens: it is a way of reading the list rather than a decision
   * about one payment, so an operator who prefers to scan by date should not have to say so again
   * for every row.
   */
  const [candidateSort, setCandidateSort] = useState<'match' | 'date'>('match');

  // Allocation editing. Saved to the record rather than held locally: these records post
  // themselves when the deposit arrives, so an edit kept in the browser would never be used.
  const [editTarget, setEditTarget] = useState<RemittanceRecord | null>(null);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [savingAllocation, setSavingAllocation] = useState(false);

  /**
   * Deposits that could still settle something.
   *
   * Uncleared money cannot confirm a payment, and a dismissed deposit has been ruled
   * not-receivable, so neither belongs in a suggestion.
   */
  const openDeposits = useMemo(
    () =>
      bankTransactions.filter(
        (deposit) => deposit.bankStatus === 'POSTED' && isDepositOpenForLinking(deposit)
      ),
    [bankTransactions]
  );

  /**
   * The other payments each record arrived with, keyed by the document that produced them.
   *
   * Waiting records only — the group that a deposit could still settle. A sibling already settled
   * or posted is no longer part of what needs confirming, and counting it would have the picker
   * offer a total the deposit cannot be expected to match.
   */
  const batchSiblings = useMemo(() => {
    const groups = new Map<string, RemittanceRecord[]>();
    for (const record of awaitingSettlementItems) {
      if (!record.batchId) {
        continue;
      }
      const existing = groups.get(record.batchId);
      if (existing) {
        existing.push(record);
      } else {
        groups.set(record.batchId, [record]);
      }
    }
    return groups;
  }, [awaitingSettlementItems]);

  const rows = useMemo<AwaitingSettlementRow[]>(() => {
    const now = Date.now();
    return awaitingSettlementItems.map((record) => {
      const waiting = daysWaiting(record, now);
      const batch = record.batchId ? batchSiblings.get(record.batchId) ?? [record] : [record];
      const batchTotal = Math.round(batch.reduce((sum, r) => sum + r.amount, 0) * 100) / 100;
      return {
        id: record.id,
        payerName: record.payerName ?? 'Unknown payer',
        // buyerName is only on records written since it was added; the matched invoices carry the
        // same name, so older records still show a customer rather than a blank.
        buyerName: record.buyerName ?? record.matchedInvoices[0]?.customerName ?? null,
        buyerId: record.buyerId ?? null,
        amount: record.amount,
        adviceDate: record.adviceDate ?? record.paymentDate ?? null,
        waiting,
        overdue: waiting !== null && waiting > SETTLEMENT_WINDOW_DAYS,
        channel: record.channel,
        matchStatus: record.matchStatus,
        settlementStatus: record.settlementStatus,
        matchConfidence: record.matchConfidence,
        matchConfidenceFactors: record.matchConfidenceFactors ?? [],
        matchedInvoices: record.matchedInvoices,
        batch,
        batchTotal,
        fileName: record.sourceFile?.name ?? null,
        candidates: rankCandidates(record, openDeposits, batchTotal),
        record,
      };
    });
  }, [awaitingSettlementItems, openDeposits, batchSiblings]);

  const summaryCards = useMemo<DillSummaryCard[]>(() => {
    const total = rows.reduce((sum, row) => sum + row.amount, 0);
    const overdue = rows.filter((row) => row.overdue);
    const overdueTotal = overdue.reduce((sum, row) => sum + row.amount, 0);

    return [
      {
        label: 'Awaiting confirmation',
        value: formatCurrency(total),
        subLabel: `${rows.length} record(s)`,
        icon: <Clock size={16} />,
      },
      {
        label: 'Overdue',
        value: formatCurrency(overdueTotal),
        subLabel: `${overdue.length} past ${SETTLEMENT_WINDOW_DAYS} days`,
        icon: <Warning size={16} />,
        variant: overdue.length > 0 ? 'warning' : 'default',
      },
    ];
  }, [rows]);

  // Both reset the expansion, so a selection made for one payment can never be carried onto
  // the next one opened.
  const openLinkModal = useCallback((row: AwaitingSettlementRow) => {
    setLinkTarget(row);
    setExpandedDepositId(null);
    setLinkSelection(new Set());
  }, []);

  const closeLinkModal = useCallback(() => {
    setLinkTarget(null);
    setExpandedDepositId(null);
    setLinkSelection(new Set());
  }, []);

  /**
   * Opens a deposit's payment list, ticking the payment this modal was opened from.
   *
   * That payment is the one the operator has already decided about, so it starts selected and the
   * expansion is only ever about adding to it. Reset on every open rather than remembered, so a
   * selection made against one deposit cannot be carried onto another by accident.
   */
  const toggleDepositExpanded = useCallback(
    (depositId: string) => {
      if (expandedDepositId === depositId) {
        setExpandedDepositId(null);
        setLinkSelection(new Set());
        return;
      }
      setExpandedDepositId(depositId);
      setLinkSelection(new Set(linkTarget ? [linkTarget.id] : []));
    },
    [expandedDepositId, linkTarget]
  );

  /**
   * The candidate list in the order the operator asked for.
   *
   * `rankCandidates` already ordered them by closeness, so that case reuses its array untouched.
   * Newest first is a copy — the ranked array is memoised on the row and sorting it in place
   * would reorder what every other row is holding.
   *
   * Descending by the bank's own date string, which is ISO and so sorts lexically. Newest first
   * because a deposit that landed this morning is the one an operator linking by hand is usually
   * looking at.
   */
  const sortedCandidates = useMemo(() => {
    if (!linkTarget) {
      return [];
    }
    if (candidateSort === 'match') {
      return linkTarget.candidates;
    }
    return [...linkTarget.candidates].sort((a, b) => b.deposit.date.localeCompare(a.deposit.date));
  }, [linkTarget, candidateSort]);

  const toggleLinkSelection = useCallback((recordId: string) => {
    setLinkSelection((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  }, []);

  /**
   * Links a deposit to the payments named, and only those.
   *
   * The ids are passed in rather than derived from the batch, because "this payment" and "these
   * payments" are different decisions and only the operator knows which they are making. A bulk
   * deposit covering a file of checks is usually all of them — but "usually" is not a mandate, and
   * one check of a file being applied separately is a normal thing to want.
   */
  const handleLink = useCallback(
    async (deposit: BankTransactionRecord, remittanceIds: string[]) => {
      if (!supplierId || !linkTarget || remittanceIds.length === 0) {
        return;
      }
      setLinking(true);
      try {
        const result = await dispatch(
          linkBankTransactionThunk({ supplierId, bankTransactionId: deposit.id, remittanceIds })
        );

        if (result.meta.requestStatus === 'fulfilled') {
          const { settled } = result.payload as { settled: number };
          openDillToast({
            message:
              settled > 1
                ? `Deposit linked to ${settled} payments. They still need review before they post.`
                : 'Deposit linked. The payment still needs review before it posts.',
            type: 'SUCCESS',
          });
          closeLinkModal();
        } else {
          openDillToast({
            message: (result.payload as string) ?? 'Failed to link the deposit.',
            type: 'ERROR',
          });
        }
      } finally {
        setLinking(false);
      }
    },
    [dispatch, supplierId, linkTarget, closeLinkModal]
  );

  /**
   * The operator's agreement to whatever this allocation leaves over.
   *
   * Recorded here even though this queue never posts: the record posts from 1-Click Review once
   * the bank confirms it, and that post refuses a remainder nobody agreed to. An allocation saved
   * here without the answer would arrive there already blocked.
   */
  const [postRemainderAsCredit, setPostRemainderAsCredit] = useState(false);

  const openEditModal = useCallback((record: RemittanceRecord) => {
    setEditTarget(record);
    const initial: Record<string, number> = {};
    record.matchedInvoices.forEach((invoice) => {
      initial[invoice.invoiceId] = invoice.suggestedAmount ?? invoice.openBalance;
    });
    setAllocations(initial);
    setInvoiceSearch('');
    setPostRemainderAsCredit(record.openCreditApproved === true);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditTarget(null);
    setAllocations({});
    setInvoiceSearch('');
    setPostRemainderAsCredit(false);
  }, []);

  const handleAllocationChange = useCallback((invoiceId: string, value: string) => {
    setAllocations((prev) => ({ ...prev, [invoiceId]: parseFloat(value) || 0 }));
  }, []);

  const totalAllocated = useMemo(
    () => Object.values(allocations).reduce((sum, value) => sum + (value || 0), 0),
    [allocations]
  );

  /**
   * Invoices offered for allocation.
   *
   * Restricted to the ones already matched unless the record has no customer, in which case there
   * is nothing to restrict by — the same rule 1-Click Review applies, so the two editors do not
   * disagree about what may be allocated.
   */
  const editableInvoices = useMemo(() => {
    if (!editTarget) {
      return [];
    }
    const matchedIds = editTarget.matchedInvoices.map((invoice) => invoice.invoiceId);
    const forCustomer = openInvoices.filter(
      (invoice) => matchedIds.includes(invoice.id) || !editTarget.buyerId
    );

    if (!invoiceSearch) {
      return forCustomer;
    }
    const search = invoiceSearch.toLowerCase();
    return forCustomer.filter(
      (invoice) =>
        invoice.docNumber?.toLowerCase().includes(search) ||
        invoice.customerName?.toLowerCase().includes(search) ||
        invoice.poNumber?.toLowerCase().includes(search)
    );
  }, [editTarget, invoiceSearch, openInvoices]);

  const handleSaveAllocation = useCallback(async () => {
    if (!supplierId || !editTarget) {
      return;
    }
    setSavingAllocation(true);
    try {
      const result = await dispatch(
        updateRemittanceAllocationsThunk({
          supplierId,
          remittanceId: editTarget.id,
          allocations: Object.entries(allocations)
            .filter(([, amount]) => amount > 0)
            .map(([invoiceId, amount]) => {
              const invoice = openInvoices.find((candidate) => candidate.id === invoiceId);
              const matched = editTarget.matchedInvoices.find(
                (candidate) => candidate.invoiceId === invoiceId
              );
              return {
                invoiceId,
                docNumber: invoice?.docNumber ?? matched?.docNumber ?? '',
                amount,
                invoiceAmount: invoice?.amount ?? matched?.invoiceAmount ?? null,
                openBalance: invoice?.balance ?? matched?.openBalance ?? null,
              };
            }),
          postRemainderAsCredit,
        })
      );

      if (result.meta.requestStatus === 'fulfilled') {
        const { allocated, unapplied } = result.payload as {
          allocated: number;
          unapplied: number;
        };
        openDillToast({
          message:
            unapplied > 0
              ? allocated > 0
                ? `Allocation saved. ${formatCurrency(
                    unapplied
                  )} is still unallocated and will post as unapplied cash.`
                : `Saved. The whole ${formatCurrency(
                    unapplied
                  )} is signed off to post as open credit on the account.`
              : 'Allocation saved. It will be applied when the deposit is confirmed.',
          type: 'SUCCESS',
        });
        closeEditModal();
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to save the allocation.',
          type: 'ERROR',
        });
      }
    } finally {
      setSavingAllocation(false);
    }
  }, [
    dispatch,
    supplierId,
    editTarget,
    allocations,
    openInvoices,
    closeEditModal,
    postRemainderAsCredit,
  ]);

  const confirmDelete = useDeleteRemittance(supplierId);

  const rowActions = useMemo<DillRowAction<AwaitingSettlementRow>[]>(
    () => [
      {
        key: 'editAllocation',
        label: 'Edit allocation',
        icon: <PencilSimple size={14} />,
        onClick: (row) => openEditModal(row.record),
      },
      {
        key: 'findDeposit',
        label: 'Find deposit',
        icon: <LinkSimple size={14} />,
        onClick: (row) => openLinkModal(row),
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <Trash size={14} />,
        variant: 'danger',
        onClick: (row) => confirmDelete(row.record),
      },
    ],
    [openEditModal, openLinkModal, confirmDelete]
  );

  const columns = useMemo<DillColumn<AwaitingSettlementRow>[]>(
    () => [
      {
        key: 'payerName',
        label: 'Payer',
        sticky: true,
        width: 200,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.payerName,
      },
      {
        /**
         * Which Dill customer gets credited, as distinct from the Payer column's "who the document
         * said paid". They frequently differ in wording, and where they differ it is this one that
         * decides where the money lands — so an operator can spot a wrong attribution here rather
         * than after posting.
         */
        key: 'buyerName',
        label: 'Dill customer',
        width: 190,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.buyerName ?? '',
        render: (_value, row) =>
          row.buyerName ? (
            <span className="text-xs text-gray-700">{row.buyerName}</span>
          ) : (
            // A record with no customer cannot settle automatically, so this is worth flagging
            // rather than leaving as an empty cell.
            <span className="text-xs text-amber-600">Unidentified</span>
          ),
      },
      {
        key: 'amount',
        label: 'Amount',
        numeric: true,
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'range',
        render: (value) => formatCurrency(value as number),
      },
      {
        key: 'adviceDate',
        label: 'Advice date',
        width: 130,
        sortable: true,
        filterable: true,
        filterType: 'dateRange',
        render: (value) => (value ? formatDate(value as string) : '—'),
      },
      {
        key: 'matchedInvoices',
        label: 'Matched invoices',
        width: 190,
        sortable: true,
        getSortValue: (row) => row.matchedInvoices.length,
        /**
         * What the payment will do when it settles.
         *
         * These records are already allocated — that is what keeps them out of Exceptions — so
         * the allocation is reviewable now rather than only after the deposit lands. It also
         * distinguishes the two reasons a row is stuck: waiting on the bank, or waiting on a
         * human to agree with the allocation.
         */
        render: (_value, row) => {
          const invoices = row.matchedInvoices;
          if (invoices.length === 0) {
            return <span className="text-xs text-gray-400">Unapplied</span>;
          }
          const totalSuggested = invoices.reduce((sum, inv) => sum + (inv.suggestedAmount ?? 0), 0);
          // Cash the allocation does not account for still has to go somewhere on posting.
          const unapplied = Math.round((row.amount - totalSuggested) * 100) / 100;

          return (
            <MatchedInvoicesTooltip invoices={invoices}>
              {({ onMouseEnter, onMouseLeave, ref }) => (
                <div
                  ref={ref}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
                  className="flex flex-col leading-tight cursor-pointer group">
                  <span className="flex items-center gap-1 text-xs text-gray-700">
                    <CaretDown
                      size={12}
                      className="text-gray-400 group-hover:text-blue-500 transition-colors"
                    />
                    {invoices.length} invoice{invoices.length === 1 ? '' : 's'} ·{' '}
                    {formatCurrency(totalSuggested)}
                  </span>
                  {unapplied !== 0 && (
                    <span className="text-[10px] text-amber-600 pl-4">
                      {formatCurrency(Math.abs(unapplied))} {unapplied > 0 ? 'unapplied' : 'over'}
                    </span>
                  )}
                </div>
              )}
            </MatchedInvoicesTooltip>
          );
        },
      },
      {
        key: 'waiting',
        label: 'Waiting',
        numeric: true,
        width: 100,
        sortable: true,
        defaultSortDir: 'desc',
        render: (value, row) =>
          value === null ? (
            '—'
          ) : (
            <span className={row.overdue ? 'font-semibold text-red-600' : ''}>
              {value as number}d
            </span>
          ),
      },
      {
        key: 'settlementStatus',
        label: 'Status',
        width: 190,
        filterable: true,
        // Overdue is not repeated here: the red day count in Waiting already says it, and the
        // summary card carries the total.
        render: (value) => (
          <SettlementBadge status={value as RemittanceRecord['settlementStatus']} />
        ),
      },
      {
        key: 'candidates',
        label: 'Possible deposit',
        width: 200,
        sortable: true,
        // Sorting on an array of objects is meaningless; the count is what an operator wants to
        // bring to the top.
        getSortValue: (row) => row.candidates.filter((candidate) => candidate.near).length,
        render: (_value, row) => {
          const near = row.candidates.filter((candidate) => candidate.near);
          if (near.length === 0) {
            return <span className="text-xs text-gray-400">None in feed</span>;
          }
          if (near.length > 1) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset bg-blue-50 text-blue-700 ring-blue-600/20">
                {near.length} possible
              </span>
            );
          }
          const [only] = near;
          return (
            <div className="flex flex-col leading-tight">
              <span className="text-xs font-medium text-gray-900">
                {formatCurrency(only.deposit.amount)} · {formatDate(only.deposit.date)}
              </span>
              {only.delta !== 0 && (
                <span className="text-[10px] text-amber-600">
                  off by {formatCurrency(Math.abs(only.delta))}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'channel',
        label: 'Source',
        width: 110,
        filterable: true,
        render: (value) => <ChannelBadge channel={value as RemittanceRecord['channel']} />,
      },
      {
        key: 'matchConfidence',
        label: 'Match confidence',
        width: 150,
        sortable: true,
        render: (value, row) => (
          <ConfidenceBadge
            confidence={value as number | null}
            factors={row.matchConfidenceFactors}
          />
        ),
      },
    ],
    []
  );

  return (
    <div className="flex flex-col w-full h-full min-h-0">
      <div className="flex items-start justify-between gap-4 px-1 py-3">
        <p className="text-sm text-gray-500">
          {bankFeedLinked
            ? 'Payments we have advice for, waiting on the matching deposit to appear in your bank feed. They move to 1-Click Review on their own once the money is confirmed. Where a deposit is close but not exact — a wire fee, a short-pay — link it by hand.'
            : 'Payments we have advice for, with no bank feed to confirm them against. Connect a bank to have these settle and post themselves.'}
        </p>
        <DillButton
          onClick={() => setAddingRecord(true)}
          label="Record a payment"
          variant="secondary"
          icon={<Plus size={14} weight="bold" />}
          height={34}
          className="shrink-0"
        />
      </div>

      {rows.some((row) => row.overdue) && (
        <div className="flex items-start gap-2 px-3 py-2 mb-2 border border-amber-200 rounded-lg mx-1 bg-amber-50">
          <Warning size={14} className="mt-0.5 shrink-0 text-amber-600" />
          <p className="text-xs leading-relaxed text-amber-800">
            Some of these have waited longer than the matcher's window. Use <b>Find deposit</b>{' '}
            where a credit is close but not exact — a wire fee, a short-pay, a date outside the
            window. If nothing in the feed can be it, the payer needs chasing or the advice is a
            duplicate and should be deleted; a payment cannot post until its deposit is confirmed.
          </p>
        </div>
      )}

      <DillTable<AwaitingSettlementRow>
        className="flex-1 min-h-0"
        columns={columns}
        data={rows}
        rowKey={(row) => row.id}
        search={true}
        searchPlaceholder="Search payer..."
        striped={true}
        hoverVariant="default"
        emptyMessage="Nothing is waiting on the bank. Every advice has been confirmed."
        summaryCards={summaryCards}
        defaultSort={{ key: 'waiting', dir: 'desc' }}
        loading={loading}
        rowActions={rowActions}
        rowActionsMode="always"
        stickyRowActions={true}
        countLabel="records"
      />

      <DillModal
        open={!!linkTarget}
        onClose={closeLinkModal}
        title="Link a deposit to this payment"
        subtitle={
          linkTarget
            ? `${linkTarget.payerName} · ${formatCurrency(linkTarget.amount)}${
                linkTarget.adviceDate ? ` · advice ${formatDate(linkTarget.adviceDate)}` : ''
              }`
            : undefined
        }
        maxWidth="620px"
        footer={<DillButton onClick={closeLinkModal} label="Cancel" variant="ghost" />}>
        {/*
          Nested rather than `linkTarget && …`, so the branch below has linkTarget narrowed to
          non-null. It reads the row's batch and id throughout, and the modal only renders when
          there is a row.
        */}
        {!linkTarget ? null : linkTarget.candidates.length === 0 ? (
          <p className="text-sm text-gray-500">
            There are no unlinked deposits in your bank feed. Until one arrives there is nothing to
            confirm this payment against.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-500">
              Cleared deposits not yet linked to any payment. Linking confirms the money arrived; it
              does not skip the allocation review if the match still needs one.
              {linkTarget.batch.length > 1 && (
                <>
                  {' '}
                  This payment arrived with {linkTarget.batch.length - 1} other
                  {linkTarget.batch.length > 2 ? 's' : ''} in{' '}
                  {linkTarget.fileName ?? 'the same file'}, which the bank may have taken as one
                  deposit. <span className="font-medium text-gray-700">Link</span> settles this
                  payment alone — open a deposit to settle more of the file against it.
                </>
              )}
            </p>

            {/*
              Two ways to find a deposit, because they answer different questions. Closest amount
              is how you identify the right one; newest first is how you scan when several are
              plausible, or when the amount was never going to line up.
            */}
            <div className="flex items-center gap-1.5 pb-0.5">
              <span className="text-xs text-gray-400">Sort</span>
              {(
                [
                  ['match', 'Closest amount'],
                  ['date', 'Newest first'],
                ] as const
              ).map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setCandidateSort(key)}
                  className={`px-2 py-0.5 rounded-md text-xs font-medium transition-colors ${
                    candidateSort === key
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                  }`}>
                  {label}
                </button>
              ))}
            </div>

            {sortedCandidates.map(({ deposit, delta, batchDelta, dayGap, near, coversFile }) => {
              const expandable = linkTarget.batch.length > 1;
              const expanded = expandedDepositId === deposit.id;
              const selectedTotal =
                Math.round(
                  linkTarget.batch
                    .filter((record) => linkSelection.has(record.id))
                    .reduce((sum, record) => sum + record.amount, 0) * 100
                ) / 100;
              // What this deposit still owes, so a deposit part-linked on an earlier visit asks
              // for the remainder rather than for its face value all over again.
              const available = depositUnaccountedAmount(deposit);
              const alreadyLinked = Math.round((deposit.amount - available) * 100) / 100;
              const unaccounted = Math.round((available - selectedTotal) * 100) / 100;

              return (
                <div
                  key={deposit.id}
                  className={`rounded-lg border ${
                    near ? 'bg-blue-50/50 border-blue-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex items-center justify-between gap-3 p-3">
                    <div className="flex items-center min-w-0 gap-2">
                      {/*
                          Expansion is offered only when there is something to choose between —
                          a lone payment has no other candidate to add.
                        */}
                      {expandable && (
                        <button
                          type="button"
                          onClick={() => toggleDepositExpanded(deposit.id)}
                          aria-label={expanded ? 'Hide payments' : 'Choose payments'}
                          className="p-0.5 text-gray-400 rounded hover:text-gray-700 hover:bg-white/60 shrink-0">
                          <CaretDown
                            size={14}
                            weight="bold"
                            className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {formatCurrency(deposit.amount)} · {describeDeposit(deposit)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(deposit.date)}
                          {dayGap !== null && ` · ${dayGap}d from advice`}
                          {/*
                              A deposit part-linked earlier. Without this its amount looks far too
                              large for what is on offer, and the "off by" below reads as wrong.
                            */}
                          {alreadyLinked > 0 && (
                            <span className="text-gray-600">
                              {' '}
                              · {formatCurrency(alreadyLinked)} already linked,{' '}
                              {formatCurrency(available)} left
                            </span>
                          )}
                          {/*
                              Which reading made this a candidate. A bulk deposit is "off by" a lot
                              against one check and exact against the file, and saying only the
                              first would have the operator dismiss the right answer.
                            */}
                          {coversFile ? (
                            <span className="text-blue-700">
                              {' '}
                              · covers all {linkTarget.batch.length} payments in this file
                              {batchDelta !== 0 &&
                                `, off by ${formatCurrency(Math.abs(batchDelta))}`}
                            </span>
                          ) : (
                            delta !== 0 && (
                              <span className="text-amber-600">
                                {' '}
                                · off by {formatCurrency(Math.abs(delta))}
                              </span>
                            )
                          )}
                        </p>
                      </div>
                    </div>
                    {/*
                        Collapsed, this links the payment the modal was opened from and nothing
                        else. Expanded, the action moves into the panel below, where what it will
                        settle is on screen.
                      */}
                    {!expanded && (
                      <DillButton
                        onClick={() => handleLink(deposit, [linkTarget.id])}
                        label={linking ? 'Linking...' : 'Link'}
                        variant={near ? 'primary' : 'secondary'}
                        isLoading={linking}
                        height={32}
                      />
                    )}
                  </div>

                  {expanded && (
                    <div className="px-3 pb-3 border-t border-black/5">
                      <p className="pt-2.5 pb-1.5 text-xs text-gray-500">
                        Which of {linkTarget.fileName ?? 'this file'}&rsquo;s payments did this
                        deposit pay for?
                      </p>
                      {linkTarget.batch.map((record) => (
                        <button
                          type="button"
                          key={record.id}
                          onClick={() => toggleLinkSelection(record.id)}
                          className="flex items-center justify-between w-full gap-3 py-1.5 text-left">
                          <div className="flex items-center min-w-0 gap-2">
                            <input
                              type="checkbox"
                              checked={linkSelection.has(record.id)}
                              readOnly
                              className="w-4 h-4 rounded pointer-events-none accent-blue-600"
                            />
                            <span className="text-sm text-gray-900 truncate">
                              {record.payerName ?? 'Unknown payer'}
                              {record.checkNumber && (
                                <span className="ml-2 text-xs text-gray-500">
                                  check {record.checkNumber}
                                </span>
                              )}
                              {record.id === linkTarget.id && (
                                <span className="ml-2 text-[10px] font-medium text-gray-500">
                                  this payment
                                </span>
                              )}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {formatCurrency(record.amount)}
                          </span>
                        </button>
                      ))}
                      <div className="flex items-center justify-between gap-3 pt-2.5 mt-1.5 border-t border-black/5">
                        {/*
                            The sum, so an operator can see when the selection accounts for the
                            deposit rather than working it out across the rows above.
                          */}
                        <p className="text-xs text-gray-600">
                          {linkSelection.size} selected · {formatCurrency(selectedTotal)} of{' '}
                          {formatCurrency(available)}
                          {unaccounted !== 0 && (
                            <span className="text-amber-600">
                              {' '}
                              ·{' '}
                              {unaccounted > 0
                                ? `${formatCurrency(unaccounted)} unaccounted`
                                : `${formatCurrency(Math.abs(unaccounted))} over`}
                            </span>
                          )}
                        </p>
                        <DillButton
                          onClick={() => handleLink(deposit, [...linkSelection])}
                          label={
                            linking
                              ? 'Linking...'
                              : linkSelection.size > 1
                              ? `Link ${linkSelection.size} payments`
                              : 'Link payment'
                          }
                          variant="primary"
                          isLoading={linking}
                          isDisabled={linkSelection.size === 0}
                          height={32}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DillModal>

      {/*
        No posting from here, and there is no longer anywhere in this queue that posts. Cash is
        applied once the bank confirms it, so a record with no linked deposit is not postable at all
        — the modal's own `blockedReason` says so and disables both buttons, and leaving
        `onAdjustAndPost` off means it renders Save as its only action.
      */}
      <AdjustAllocationModal
        adjustItem={editTarget}
        title="Edit allocation"
        allocations={allocations}
        onAllocationChange={handleAllocationChange}
        totalAllocated={totalAllocated}
        filteredOpenInvoices={editableInvoices}
        invoiceSearch={invoiceSearch}
        onInvoiceSearchChange={setInvoiceSearch}
        onClose={closeEditModal}
        onSaveOnly={handleSaveAllocation}
        saveLabel={savingAllocation ? 'Saving...' : 'Save allocation'}
        isValidAllocation={false}
        // Open credit with nothing allocated is a save too: see the same gate in ExceptionsQueue.
        hasAllocations={(totalAllocated > 0 || postRemainderAsCredit) && !savingAllocation}
        onAllocationsReset={() => setAllocations({})}
        onPostRemainderChange={setPostRemainderAsCredit}
        postRemainderAsCredit={postRemainderAsCredit}
      />

      <AddRemittanceModal open={addingRecord} onClose={() => setAddingRecord(false)} />
    </div>
  );
};
